import React from 'react';
import { Box, Stack, Text } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import type { ProjectContext } from '@atlassian/jira-router-resources-project-context/src/services/project-context/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { NewTabLink } from '../../../new-tab-link/index.tsx';
import { messages } from './messages.tsx';

export type FindFieldHelpLinkProps = {
	isEfaEnabled: boolean;
	isGlobalAdmin: boolean;
	readonly project: ProjectContext;
};

const Wrapper = ({ children }: { readonly children: React.ReactNode }) => (
	<Box padding="space.200">
		<Stack space="space.100" alignInline="start">
			{children}
		</Stack>
	</Box>
);

const LinkToGlobalSettings = () => {
	const { formatMessage } = useIntl();
	return (
		<Wrapper>
			<Text>{formatMessage(messages.helpMessage)}</Text>
			<NewTabLink
				href={
					fg('centralized-fields-page-m1')
						? '/jira/settings/issues/fields'
						: '/secure/admin/ViewCustomFields.jspa'
				}
			>
				{formatMessage(messages.helpLink)}
			</NewTabLink>
		</Wrapper>
	);
};

const ContactAdminLink = () => {
	const { formatMessage } = useIntl();
	return (
		<Wrapper>
			<Text>{formatMessage(messages.helpMessage)}</Text>
			<NewTabLink href="/secure/ContactAdministrators!default.jspa">
				{formatMessage(messages.contactAdminLink)}
			</NewTabLink>
		</Wrapper>
	);
};

const FieldsPageLink = ({ project }: { readonly project: ProjectContext }) => {
	const { formatMessage } = useIntl();
	return (
		<Wrapper>
			<Text>{formatMessage(messages.findGlobalField)}</Text>
			<NewTabLink href={`/jira/software/projects/${project.projectKey}/settings/fields`}>
				{formatMessage(messages.helpLinkToFieldsPage)}
			</NewTabLink>
		</Wrapper>
	);
};

export const SidebarFindFieldHelpLinkWithEfaEnabled = ({
	project,
	isEfaEnabled,
	isGlobalAdmin,
}: FindFieldHelpLinkProps) => {
	// We're making assumption here that every simplified project supports EFA. I'm afraid that might bite us later, perhaps this should be a ProjectFeature instead?
	if (isEfaEnabled && project?.simplified) {
		return <FieldsPageLink project={project} />;
	}

	if (isGlobalAdmin) {
		return <LinkToGlobalSettings />;
	}

	return <ContactAdminLink />;
};
